/**
 * TranscriptCoachmark — the in-video tutorial coachmark that points at the live
 * transcript, shared by the lesson player (LessonTutorialOverlay) and the feed
 * (FeedTutorialCoachmark) so the two can't drift.
 *
 * Mirrors the native SpotlightTutorial (SpotlightTutorialController.swift): it
 * targets a REAL transcript element, dims the rest of the screen with a rounded
 * transparent cutout over the target (70% black — the iOS "spotlight", which
 * makes the target read brighter rather than recolouring it), anchors the
 * instruction pill ABOVE it, and points/animates a finger AT it:
 *   - word_click      → first visible word: finger bounces up at it.
 *   - phrase_highlight → first visible line: finger slides left→right across it.
 *
 * The target is re-measured every frame so the overlays track the transcript as
 * it moves (captions expand with a height transition when the tutorial forces
 * them on; the lesson transcript also auto-scrolls). Only the first VISIBLE row
 * of the on-screen reel is targeted, so the finger never lands on an offscreen
 * feed neighbor or a row clipped out of the caption window.
 *
 * Non-blocking: every layer is pointer-events:none so the learner completes the
 * phase by actually tapping the word / dragging the phrase underneath (the native
 * tutorial likewise completes on the real gesture).
 */
import { useEffect, useId, useState } from "react";
import { colors } from "@/ui";
import BouncingPointer from "./BouncingPointer";
import pointerImage from "../assets/click-word.png";
import {
  PHASE_TITLES,
  type TranscriptCoachmarkPhase,
} from "./transcriptCoachmarkPhases";

/** On-screen frame (viewport coords) of the coachmark's transcript target. */
interface TargetFrame {
  left: number;
  top: number;
  width: number;
  height: number;
}

const FINGER_SIZE = 44;
/** Overlap (px) the finger tip extends up into the target so it points AT it. */
const FINGER_OVERLAP = 2;
/** Padding of the spotlight cutout around the target chips. */
const CUTOUT_PAD = 4;
/** Gap above the target the instruction pill is anchored (keeps it clear of the
 *  transcript, higher up the screen). */
const PILL_GAP = 56;
/** Min change (px) before the measured target is treated as moved (avoids
 *  re-rendering every animation frame when nothing actually shifted). */
const FRAME_EPS = 0.5;

/** Pick the transcript region currently on-screen (the active reel / lesson). The
 *  feed keeps prev/next lesson transcripts mounted offscreen to the side, so a
 *  vertical-only test could match a neighbour — require the region's horizontal
 *  center to be within the viewport too. */
function findVisibleTranscript(): HTMLElement | null {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  return (
    Array.from(
      document.querySelectorAll<HTMLElement>('[aria-label="Transcript"]'),
    ).find((r) => {
      const rect = r.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) return false;
      const cx = rect.left + rect.width / 2;
      const visibleH = Math.min(rect.bottom, vh) - Math.max(rect.top, 0);
      return cx >= 0 && cx <= vw && visibleH > 20;
    }) ?? null
  );
}

/** The colored chip inside a transcript word slot (excludes the underline-reserve
 *  whitespace below it, so the cutout hugs the visible pill, not the tall slot). */
function chipRect(slot: HTMLElement): DOMRect {
  const pill = slot.querySelector<HTMLElement>("span > span");
  return (pill ?? slot).getBoundingClientRect();
}

/**
 * Measure the coachmark target from the on-screen transcript:
 *  - word_click      → the first VISIBLE word's chip.
 *  - phrase_highlight → the first VISIBLE line's chips (union).
 * "Visible" = the word's vertical center is inside the (masked) transcript
 * window, so a row scrolled out of the 2-line caption view is never targeted.
 * Returns null when nothing measurable yet / a degenerate rect (caller retries).
 */
function measureTarget(phase: TranscriptCoachmarkPhase): TargetFrame | null {
  const region = findVisibleTranscript();
  if (!region) return null;
  const cRect = region.getBoundingClientRect();
  const wordEls = Array.from(
    region.querySelectorAll<HTMLElement>("[data-word-index]"),
  );
  const visible = wordEls.filter((el) => {
    const r = el.getBoundingClientRect();
    const cy = (r.top + r.bottom) / 2;
    return cy >= cRect.top && cy <= cRect.bottom;
  });
  if (visible.length === 0) return null;

  const rowTop = Math.min(...visible.map((el) => el.offsetTop));
  const rowEls = visible
    .filter((el) => el.offsetTop === rowTop)
    .sort((a, b) => Number(a.dataset.wordIndex) - Number(b.dataset.wordIndex));
  const chips = phase === "word_click" ? rowEls.slice(0, 1) : rowEls;

  let left = Infinity;
  let right = -Infinity;
  let top = Infinity;
  let bottom = -Infinity;
  for (const el of chips) {
    const r = chipRect(el);
    left = Math.min(left, r.left);
    right = Math.max(right, r.right);
    top = Math.min(top, r.top);
    bottom = Math.max(bottom, r.bottom);
  }
  const width = right - left;
  const height = bottom - top;
  // Guard against an unlaid-out 0×0 rect (element present but not yet painted).
  if (!Number.isFinite(left) || width <= 0 || height <= 0) return null;
  return { left, top, width, height };
}

function sameFrame(a: TargetFrame | null, b: TargetFrame): boolean {
  return (
    a !== null &&
    Math.abs(a.left - b.left) < FRAME_EPS &&
    Math.abs(a.top - b.top) < FRAME_EPS &&
    Math.abs(a.width - b.width) < FRAME_EPS &&
    Math.abs(a.height - b.height) < FRAME_EPS
  );
}

/** Track the transcript target every frame; only updates state when it moves. */
function useTranscriptTarget(phase: TranscriptCoachmarkPhase): TargetFrame | null {
  const [target, setTarget] = useState<TargetFrame | null>(null);

  useEffect(() => {
    let raf = 0;
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      const next = measureTarget(phase);
      if (next) setTarget((prev) => (sameFrame(prev, next) ? prev : next));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => {
      cancelled = true;
      cancelAnimationFrame(raf);
    };
  }, [phase]);

  return target;
}

function TranscriptCoachmark({ phase }: { phase: TranscriptCoachmarkPhase }) {
  const target = useTranscriptTarget(phase);
  // Per-instance keyframe name so a transient second mount can't have its sweep
  // distance clobbered by another instance's global @keyframes.
  const sweepAnim = `coach-sweep-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const title = PHASE_TITLES[phase];

  // Until measured, render only the pill near the lower third so the coachmark is
  // never blank (no spotlight/finger yet — we don't know where the words are).
  if (!target) {
    return (
      <div
        className="absolute inset-x-0 z-40 flex justify-center"
        style={{ bottom: "34%", pointerEvents: "none" }}
      >
        <Pill title={title} />
      </div>
    );
  }

  const sweepMaxX = Math.max(0, target.width - FINGER_SIZE);

  return (
    <>
      {/* Spotlight: dim the whole screen with a rounded transparent cutout over
          the target (iOS SpotlightOverlayView). The big box-shadow IS the dim. */}
      <div
        aria-hidden
        style={{
          position: "fixed",
          left: target.left - CUTOUT_PAD,
          top: target.top - CUTOUT_PAD,
          width: target.width + CUTOUT_PAD * 2,
          height: target.height + CUTOUT_PAD * 2,
          borderRadius: 8,
          boxShadow: `0 0 0 9999px ${colors.blackOverlay}`,
          pointerEvents: "none",
          zIndex: 40,
        }}
      />

      {/* Instruction pill, anchored well ABOVE the target (never over the words). */}
      <div
        style={{
          position: "fixed",
          left: 0,
          right: 0,
          top: target.top - PILL_GAP,
          transform: "translateY(-100%)",
          display: "flex",
          justifyContent: "center",
          pointerEvents: "none",
          zIndex: 42,
        }}
      >
        <Pill title={title} />
      </div>

      {phase === "word_click" ? (
        // Bounce up at the first word: finger sits just under it, tip pointing up.
        <div
          style={{
            position: "fixed",
            left: target.left + target.width / 2 - FINGER_SIZE / 2,
            top: target.top + target.height - FINGER_OVERLAP,
            pointerEvents: "none",
            zIndex: 42,
          }}
        >
          <BouncingPointer size={FINGER_SIZE} direction="up" />
        </div>
      ) : (
        // Slide left→right across the first line (iOS .slideHorizontal).
        <div
          style={{
            position: "fixed",
            left: target.left,
            top: target.top,
            width: target.width,
            height: target.height,
            pointerEvents: "none",
            zIndex: 42,
          }}
        >
          <style>{`
            @keyframes ${sweepAnim} {
              0% { transform: translateX(0px); }
              50% { transform: translateX(${sweepMaxX}px); }
              100% { transform: translateX(0px); }
            }
          `}</style>
          <img
            src={pointerImage}
            alt=""
            aria-hidden
            style={{
              position: "absolute",
              left: 0,
              top: target.height - FINGER_OVERLAP,
              width: FINGER_SIZE,
              height: FINGER_SIZE,
              objectFit: "contain",
              animation: `${sweepAnim} 1600ms ease-in-out infinite`,
            }}
          />
        </div>
      )}
    </>
  );
}

function Pill({ title }: { title: string }) {
  return (
    <div
      className="px-6 py-4 rounded-3xl"
      style={{
        backgroundColor: colors.white,
        boxShadow: "0 4px 12px rgba(0,0,0,0.25)",
        maxWidth: "80%",
      }}
    >
      <span
        style={{
          fontSize: "var(--base-font-size-large)",
          fontWeight: 700,
          color: colors.text.primary,
          textAlign: "center",
        }}
      >
        {title}
      </span>
    </div>
  );
}

export default TranscriptCoachmark;
